'use strict';

Object.defineProperty(exports, Symbol.toStringTag, { value: 'Module' });

const react = require('@emotion/react');
const components_navigation_IconButton_IconButton = require('../IconButton/IconButton.cjs.js');
require('../../../theme/Colors.cjs.js');
require('../../../theme/Shadows.cjs.js');
const theme_Typography = require('../../../theme/Typography.cjs.js');
require('react');
const styleEncapsulation = require('@zapier/style-encapsulation');
const utils_clamp = require('../../../utils/clamp.cjs.js');
const components_navigation_NumberButton_NumberButton = require('../NumberButton/NumberButton.cjs.js');
const styleMixins_reset = require('../../../style-mixins/reset.cjs.js');

const Styles = styleEncapsulation.labelStyles("Paginator", {
  root: (derivedState, isCentered) => [
    Object.assign(Object.assign({}, styleMixins_reset.boxSizing), {
      display: "grid",
      gap: "var(--zds-space-8)",
      // Add two to account for the previous and next buttons.
      gridTemplateColumns: `repeat(${derivedState.numberOfButtons}, 40px)`,
      alignItems: "center",
      "a, button": {
        height: "var(--zds-size-medium) !important",
        width: "var(--zds-size-medium) !important",
        font: "var(--zds-body-2) !important"
      },
      "a[data-color=primary], button[data-color=primary]": {
        backgroundColor: `var(--zds-ui-primary-weaker)`,
        color: `var(--zds-ui-primary-strongest)`,
        borderColor: `var(--zds-ui-primary-weaker)`,
        boxShadow: "none",
        "&:hover:not(:disabled)": {
          color: `var(--zds-ui-primary-strongest)`,
          borderColor: `var(--zds-ui-primary-weaker)`,
          backgroundColor: `var(--zds-ui-primary-weaker)`
        },
        "&:focus-visible:not(:disabled)": {
          "&::before": {
            border: `var(--zds-border-focus)`
          },
          color: `var(--zds-ui-primary-strongest)`,
          borderColor: `var(--zds-ui-primary-weaker)`,
          backgroundColor: `var(--zds-ui-primary-weaker)`
        }
      },
      "a[data-color=number], button[data-color=number]": {
        backgroundColor: `var(--zds-background-default)`,
        color: `var(--zds-text-weaker)`,
        borderColor: `var(--zds-stroke-weaker)`,
        boxShadow: "none",
        "&:hover:not(:disabled)": {
          color: `var(--zds-text-weaker)`,
          borderColor: `var(--zds-stroke-weaker)`,
          backgroundColor: `var(--zds-background-stronger)`
        },
        "&:focus-visible:not(:disabled)": {
          "&::before": {
            border: `var(--zds-border-focus) !important`
          },
          color: `var(--zds-text-weaker)`,
          borderColor: `var(--zds-stroke-weaker)`,
          backgroundColor: `var(--zds-background-stronger)`
        }
      },
      "a[data-color=icon-secondary], button[data-color=icon-secondary]": {
        backgroundColor: `var(--zds-background-weaker)`,
        color: `var(--zds-text-weaker)`,
        borderColor: `var(--zds-stroke-weaker)`,
        boxShadow: "none",
        "&:hover:not(:disabled)": {
          color: `var(--zds-text-weaker)`,
          borderColor: `var(--zds-stroke-weaker)`,
          backgroundColor: `var(--zds-background-stronger)`
        },
        "&:focus-visible:not(:disabled)": {
          "&::before": {
            border: `var(--zds-border-focus) !important`
          },
          color: `var(--zds-text-weaker)`,
          borderColor: `var(--zds-stroke-weaker)`,
          backgroundColor: `var(--zds-background-stronger)`
        }
      }
    }),
    derivedState.isCondensed && styleEncapsulation.withModifier("condensed", {
      gridTemplateColumns: "44px 44px min-content 44px 44px"
    }),
    isCentered && styleEncapsulation.withModifier("isCentered", {
      justifyContent: "center"
    })
  ],
  text: [
    react.css(Object.assign(Object.assign(Object.assign({}, theme_Typography.Typography.base), styleMixins_reset.boxSizing), { fontSize: 16, lineHeight: "22px", fontWeight: 600, textAlign: "center", whiteSpace: "nowrap", color: "var(--zds-text-stronger)" })),
    react.css`
      padding-left: 20px;
      padding-right: 20px;
    `
  ]
});
const PaginatorText = (props) => {
  const maxChars = `${props.numberOfPages}`.length * 2 + 4;
  return react.jsx("span", { css: Styles.text, "data-zds": true, style: {
    minWidth: `${maxChars}ch`
  } }, `${props.currentPageIndex + 1} of ${props.numberOfPages}`);
};
const Paginator = ({ currentPageIndex, pageUrls, "aria-label": ariaLabel = void 0, condenseAt = 6, firstPageUrl = void 0, isCentered = void 0, lastPageUrl = void 0, offset = 0, onPaginate = void 0 }) => {
  const numberOfPages = pageUrls.length;
  const maxPageIndex = numberOfPages - 1;
  const safeCurrentPageIndex = utils_clamp.clamp(currentPageIndex, 0, maxPageIndex);
  const prevIndex = Math.max(0, safeCurrentPageIndex - 1);
  const nextIndex = Math.min(maxPageIndex, safeCurrentPageIndex + 1);
  const isCondensed = numberOfPages >= condenseAt;
  const derivedState = {
    numberOfPages,
    isCondensed,
    numberOfButtons: numberOfPages + 2 + [firstPageUrl, lastPageUrl].filter(Boolean).length
  };
  const createOnPaginateFn = (buttonType, pageIndex, pageUrl) => {
    return (event) => {
      if (onPaginate) {
        onPaginate({
          buttonType,
          pageIndex,
          pageUrl
        }, event);
      }
    };
  };
  const content = isCondensed ? react.jsx(PaginatorText, { currentPageIndex: safeCurrentPageIndex, numberOfPages }) : pageUrls.map((pageUrl, pageIndex) => {
    const isSelected = pageIndex === safeCurrentPageIndex;
    const pageNumber = offset + pageIndex + 1;
    return react.jsx(components_navigation_NumberButton_NumberButton.NumberButton, { "aria-label": isSelected ? `Current page (${pageNumber})` : `${pageNumber}`, href: pageUrl, key: pageUrl || pageNumber, onClick: createOnPaginateFn("number", pageIndex, pageUrl), value: pageNumber, selected: isSelected, hasShadow: isSelected });
  });
  const firstPageButtonUrl = firstPageUrl || (isCondensed ? pageUrls[0] : void 0);
  const firstPageButton = (firstPageButtonUrl || isCondensed) && react.jsx(components_navigation_IconButton_IconButton.IconButton, { "aria-label": "First page", color: "icon-secondary", disabled: currentPageIndex <= 0, href: firstPageButtonUrl, icon: "arrowLineLeft", onClick: createOnPaginateFn("first", 0, firstPageButtonUrl), tooltipPosition: "southwest" });
  const lastPageButtonUrl = lastPageUrl || (isCondensed ? pageUrls[maxPageIndex] : void 0);
  const lastPageButton = (lastPageButtonUrl || isCondensed) && react.jsx(components_navigation_IconButton_IconButton.IconButton, { "aria-label": "Last page", color: "icon-secondary", disabled: safeCurrentPageIndex >= maxPageIndex, href: lastPageButtonUrl, icon: "arrowLineRight", onClick: createOnPaginateFn("last", maxPageIndex, lastPageButtonUrl), tooltipPosition: "southeast" });
  return react.jsx(
    "nav",
    { "aria-label": ariaLabel ? ariaLabel : "Pagination navigation", css: [Styles.root(derivedState, isCentered)], "data-zds": true },
    firstPageButton,
    react.jsx(components_navigation_IconButton_IconButton.IconButton, { "aria-label": `Previous page (${prevIndex + 1})`, color: "icon-secondary", disabled: safeCurrentPageIndex <= 0, href: pageUrls[prevIndex], icon: "arrowSmallLeft", onClick: createOnPaginateFn("previous", prevIndex, pageUrls[prevIndex]), tooltipPosition: "southwest" }),
    content,
    react.jsx(components_navigation_IconButton_IconButton.IconButton, { "aria-label": `Next page (${nextIndex + 1})`, color: "icon-secondary", disabled: safeCurrentPageIndex >= maxPageIndex, href: pageUrls[nextIndex], icon: "arrowSmallRight", onClick: createOnPaginateFn("next", nextIndex, pageUrls[nextIndex]), tooltipPosition: "southeast" }),
    lastPageButton
  );
};

exports.Paginator = Paginator;
//# sourceMappingURL=Paginator.cjs.js.map
